/**
 * Deep C2PA Metadata Extractor
 * Extracts comprehensive C2PA manifest data including JUMBF structures,
 * actions, validation results, signatures, and ingredients
 */

import type { ManifestStore } from '@contentauth/c2pa-web'
import type { C2PADetails, C2PAAction, ValidationResults } from '../types/metadata'

/**
 * Format binary data for display
 */
export const formatBinaryData = (data: Uint8Array | ArrayBuffer | string | undefined): string => {
  if (!data) return ''

  if (typeof data === 'string') {
    return data
  }

  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data)
  if (bytes.length === 0) return ''

  // Show size and first 8 bytes in hex for hashes
  const hexPreview = Array.from(bytes.slice(0, 8))
    .map(b => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ')

  return `(Binary data ${bytes.length} bytes) [${hexPreview}${bytes.length > 8 ? '...' : ''}]`
}

/**
 * Extract claim generator information
 */
const extractClaimGeneratorInfo = (claimGenerator: any) => {
  if (!claimGenerator) return null

  if (typeof claimGenerator === 'string') {
    return {
      name: claimGenerator,
      version: undefined,
      org_contentauth_c2_pa_rs: undefined
    }
  }

  return {
    name: claimGenerator.name || claimGenerator['@name'],
    version: claimGenerator.version,
    org_contentauth_c2_pa_rs: claimGenerator['org.contentauth.c2pa.rs']
  }
}

/**
 * Extract validation results details
 */
const extractValidationResults = (validationResults: any): ValidationResults | undefined => {
  if (!validationResults) return undefined

  const activeManifest = validationResults.activeManifest || validationResults.active_manifest
  if (!activeManifest) return undefined

  return {
    activeManifest: {
      success: activeManifest.success || [],
      failure: activeManifest.failure || [],
      informational: activeManifest.informational || []
    }
  }
}

/**
 * Extract actions from assertions
 */
const extractActions = (assertions: any[]): C2PAAction[] => {
  const actionsAssertion = assertions.find(a => a.label === 'c2pa.actions')
  if (!actionsAssertion?.data?.actions) return []

  return actionsAssertion.data.actions.map((action: any) => {
    const softwareAgent = action.softwareAgent || action.software_agent

    return {
      action: action.action,
      when: action.when,
      softwareAgent: typeof softwareAgent === 'string'
        ? softwareAgent
        : {
            name: softwareAgent?.name,
            version: softwareAgent?.version
          },
      digitalSourceType: action.digitalSourceType || action.digital_source_type,
      parameters: action.parameters,
      instanceId: action.instanceId || action.instance_id
    }
  })
}

/**
 * Extract ingredient details
 */
/*
const extractIngredients = (ingredients: any[]) => {
  return ingredients.map(ing => {
    const thumbnail = ing.thumbnail || ing.c2pa_thumbnail

    return {
      title: ing.title,
      format: ing.format,
      instanceId: ing.instanceId || ing.instance_id,
      isParent: ing.isParent || ing.is_parent || false,
      relationship: ing.relationship,
      documentId: ing.documentId || ing.document_id,
      // Thumbnail details
      thumbnailSalt: thumbnail?.salt,
      thumbnailType: thumbnail?.type || thumbnail?.format,
      thumbnailData: thumbnail?.data,
      thumbnailSize: thumbnail?.data ? thumbnail.data.length : undefined,
      // URLs and hashes
      url: ing.url,
      hash: ing.hash,
      alg: ing.alg || ing.algorithm
    }
  })
}
*/

/**
 * Extract assertion details including URLs and hashes
 */
/*
const extractAssertions = (assertions: any[]) => {
  return assertions.map(assertion => ({
    label: assertion.label,
    data: assertion.data,
    kind: assertion.kind,
    url: assertion.url,
    hash: assertion.hash,
    alg: assertion.alg || assertion.algorithm
  }))
}
*/

/**
 * Extract deep C2PA metadata from manifest store
 */
export const extractDeepC2PAMetadata = (
  manifestStore: ManifestStore,
  activeManifest: any
): Partial<C2PADetails> => {
  try {
    const validationResults = manifestStore.validation_results || (manifestStore as any).validationResults

    // Extract JUMBF manifest structure
    const jumbfData: Partial<C2PADetails> = {
      jumd_type: (manifestStore as any).jumd_type,
      jumd_label: (manifestStore as any).jumd_label || 'c2pa',
      name: activeManifest.label || activeManifest.name,
      instance_id: activeManifest.instanceId || activeManifest.instance_id,
      alg: activeManifest.alg || activeManifest.algorithm,
      hash: activeManifest.hash,
      pad: (manifestStore as any).pad
    }

    // Extract claim generator info
    const claimGeneratorInfo = extractClaimGeneratorInfo(activeManifest.claimGenerator || activeManifest.claim_generator)

    // Extract signature details
    const signatureDetails = {
      claimSignatureUrl: (activeManifest as any).claim_signature_url,
      claimSignatureAlg: (activeManifest as any).claim_signature_alg,
      claimSignatureHash: (activeManifest as any).claim_signature_hash,
      thumbnailUrl: activeManifest.thumbnail?.url,
      thumbnailHash: activeManifest.thumbnail?.hash
    }

    // Extract validation results
    const validationResultsData = extractValidationResults(validationResults)

    // Extract active manifest URLs and hashes
    const activeManifestDetails = {
      activeManifestUrl: (activeManifest as any).url || (activeManifest as any).active_manifest_url,
      activeManifestAlg: (activeManifest as any).alg || (activeManifest as any).active_manifest_alg,
      activeManifestHash: (activeManifest as any).hash || (activeManifest as any).active_manifest_hash
    }

    return {
      ...jumbfData,
      validationResults: validationResultsData,
      activeManifest: {
        ...activeManifest,
        claimGeneratorInfo,
        signatureDetails,
        ...activeManifestDetails
      }
    }
  } catch (error) {
    console.warn('Error extracting deep C2PA metadata:', error)
    return {}
  }
}

/**
 * Extract all C2PA actions with detailed parameters
 */
export const extractC2PAActions = (assertions: any[]): C2PAAction[] => {
  return extractActions(assertions)
}

/**
 * Get all validation result codes, URLs, and explanations
 */
export const getValidationArrays = (validationResults: ValidationResults | undefined) => {
  if (!validationResults?.activeManifest) {
    return {
      successCodes: [],
      successUrls: [],
      successExplanations: [],
      failureCodes: [],
      failureUrls: [],
      failureExplanations: [],
      informationalCodes: [],
      informationalUrls: [],
      informationalExplanations: []
    }
  }

  const { success = [], failure = [], informational = [] } = validationResults.activeManifest

  return {
    successCodes: success.map(s => s.code),
    successUrls: success.map(s => s.url).filter(Boolean),
    successExplanations: success.map(s => s.explanation).filter(Boolean),
    failureCodes: failure.map(f => f.code),
    failureUrls: failure.map(f => f.url).filter(Boolean),
    failureExplanations: failure.map(f => f.explanation).filter(Boolean),
    informationalCodes: informational.map(i => i.code),
    informationalUrls: informational.map(i => i.url).filter(Boolean),
    informationalExplanations: informational.map(i => i.explanation).filter(Boolean)
  }
}

/**
 * Count total C2PA fields extracted
 */
export const countC2PAFields = (c2paDetails: C2PADetails): number => {
  let count = 0

  // JUMBF structure fields
  if (c2paDetails.jumd_type) count++
  if (c2paDetails.jumd_label) count++
  if (c2paDetails.name) count++
  if (c2paDetails.instance_id) count++
  if (c2paDetails.alg) count++
  if (c2paDetails.hash) count++

  // Active manifest fields
  const manifest = c2paDetails.activeManifest
  if (manifest.title) count++
  if (manifest.format) count++
  if (manifest.claimGenerator) count++

  // Claim generator info
  if (manifest.claimGeneratorInfo?.name) count++
  if (manifest.claimGeneratorInfo?.version) count++
  if (manifest.claimGeneratorInfo?.org_contentauth_c2_pa_rs) count++

  // Signature info
  if (manifest.signatureInfo?.issuer) count++
  if (manifest.signatureInfo?.commonName) count++
  if (manifest.signatureInfo?.time) count++
  if (manifest.signatureInfo?.certSerialNumber) count++
  if (manifest.signatureInfo?.alg) count++
  if (manifest.signatureInfo?.revocationStatus !== undefined && manifest.signatureInfo?.revocationStatus !== null) count++

  // Signature details
  if (manifest.signatureDetails?.claimSignatureUrl) count++
  if (manifest.signatureDetails?.claimSignatureAlg) count++
  if (manifest.signatureDetails?.claimSignatureHash) count++
  if (manifest.signatureDetails?.thumbnailUrl) count++
  if (manifest.signatureDetails?.thumbnailHash) count++

  // Active manifest URLs
  if (manifest.activeManifestUrl) count++
  if (manifest.activeManifestAlg) count++
  if (manifest.activeManifestHash) count++

  // Ingredients (estimate 5 fields per ingredient)
  count += manifest.ingredients.length * 5

  // Assertions (estimate 2 fields per assertion)
  count += manifest.assertions.length * 2

  // Validation results
  if (c2paDetails.validationResults) {
    const results = c2paDetails.validationResults.activeManifest
    if (results) {
      count += (results.success?.length || 0) * 2
      count += (results.failure?.length || 0) * 2
      count += (results.informational?.length || 0) * 2
    }
  }

  return count
}
